/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { types, applySnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import { toJS } from 'mobx';

import * as constants from '../constants/ServiceConstants';

const identifierModel = types.model('identifierModel', {
  autobahn: types.maybeNull(types.boolean),
  graph: types.maybeNull(types.boolean)
});

const apiConfigModel = types.model('apiConfigModel', {
  searchProperties: types.maybeNull(types.array(types.string)),
  identifierSource: types.maybeNull(identifierModel)
});

const tenantDetail = types.model('TenantDetail', {
  tenantId: types.maybeNull(types.string),
  tenantKey: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  apiConfig: types.maybeNull(apiConfigModel),
  isSandbox: types.maybeNull(types.boolean)
});

const voiceDetail = types.model('VoiceDetail', {
  name: types.maybeNull(types.string),
  type: types.maybeNull(types.string),
  language: types.maybeNull(types.string),
  isDefault: types.maybeNull(types.boolean)
});

const providerDetail = types.model('ProviderDetail', {
  providerId: types.maybeNull(types.string),
  providerName: types.maybeNull(types.string),
  region: types.maybeNull(types.string),
  model: types.maybeNull(types.string),
  active: types.maybeNull(types.boolean),
  audioSupported: types.maybeNull(types.boolean),
  isInternal: types.maybeNull(types.boolean),
  voices: types.optional(types.array(voiceDetail), [])
});

const targetTenantDetail = types.model('TargetTenantDetail', {
  tenantId: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  name: types.maybeNull(types.string)
});

const TenantsModel = types
  .model('TenantsModel', {
    tenantList: types.optional(types.array(tenantDetail), []),
    selectedTenant: types.optional(tenantDetail, () => tenantDetail.create({})),
    providerList: types.optional(types.array(providerDetail), []),
    targetTenantList: types.optional(types.array(targetTenantDetail), [])
  })
  .views((self) => ({
    getTenantById(tenantId) {
      return toJS(self.tenantList.find((item) => item.tenantId === tenantId));
    }
  }))
  .actions((self) => ({
    fetchTenantsList() {
      Framework.getEventManager().publish(constants.GET_TENANT_LIST, {});
    },
    setTenantsList(response) {
      self.tenantList.replace(response);
      Framework.getEventManager().publish(constants.GET_TENANT_LIST_SUCCESS, {});
    },
    setSelectedTenant(tenantId) {
      const tenant = self.tenantList.find((item) => item.tenantId === tenantId);
      if (tenant) {
        applySnapshot(self.selectedTenant, toJS(tenant));
      }
    },
    fetchProviderList(tenantId) {
      Framework.getEventManager().publish(constants.GET_TENANT_PROVIDER_LIST, tenantId);
    },
    setTenantProviderList(response) {
      self.providerList.replace(response);
      Framework.getEventManager().publish(constants.GET_TENANT_PROVIDER_SUCCESS, {});
    },
    fetchTargetTenants(targetEnv) {
      Framework.getEventManager().publish(constants.GET_TARGET_TENANTS, { targetEnv });
    },
    setTargetTenantList(response) {
      applySnapshot(self.targetTenantList, response);
    },
    clearTargetTenantList() {
      applySnapshot(self.targetTenantList, []);
    }
  }));

export default TenantsModel;
